import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Table, Tooltip } from "antd";
import { getVotingOutcomes } from "../redux/actions/CompanyAction";
import dayjs from "dayjs";
import { emdash, RadioSelector } from "../utils/Constants/systemSetting";

export default function VotingOutcomes({ companyInfo, PDFprocessing }) {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.UserReducer);
  const { subscription } = useSelector((state) => state.CustomerReducer);
  const isTier2 = useMemo(() => {
    if (user?.tier2access || subscription?.plan?.metadata?.isTier2 === "true") {
      return true;
    }
    return false;
  }, [user, subscription]);

  const { votingOutcomes } = useSelector((state) => state.CompanyReducer);

  const filterOutTestData = true;

  useMemo(() => {
    const token = localStorage.getItem("ACCESS_TOKEN");
    const ticker = companyInfo?.Ticker;
    const CIK = companyInfo?.CIK;

    dispatch(getVotingOutcomes(JSON.stringify([{ ticker, CIK }]), token));
  }, [companyInfo, dispatch]);

  const [allCompanyVotingOutcomes, setAllCompanyVotingOutcomes] = useState({});
  const [companyVotingOutcomes, setCompanyVotingOutcomes] = useState({});
  const [votingOutcomeDateSelected, setVotingOutcomeDateSelected] =
    useState("");

  useEffect(() => {
    setAllCompanyVotingOutcomes(votingOutcomes?.[companyInfo?.Ticker] || []);
  }, [companyInfo, votingOutcomes]);

  const [votingOutcomeDatesAvailable, setVotingOutcomeDatesAvailable] =
    useState([]);

  const approvalDisclaimer =
    "Approval check for >50%. Check document details for actual result.";

  useEffect(() => {
    if (!allCompanyVotingOutcomes || !Array.isArray(allCompanyVotingOutcomes)) {
      return [];
    }
    const formattedVotingDateOptions = allCompanyVotingOutcomes
      .filter((item) => !(filterOutTestData && item?.isTestData === true))
      .map((item) => dayjs(item?.meetingDate).format("M/D/YYYY"))
      .sort();
    if (formattedVotingDateOptions.length) {
      setVotingOutcomeDateSelected(
        formattedVotingDateOptions[formattedVotingDateOptions.length - 1],
      );
    }
    setVotingOutcomeDatesAvailable(formattedVotingDateOptions);
  }, [allCompanyVotingOutcomes, filterOutTestData]);

  useEffect(() => {
    if (!allCompanyVotingOutcomes || !Array.isArray(allCompanyVotingOutcomes)) {
      setCompanyVotingOutcomes({});
      return;
    }
    const currentVotingOutcome = allCompanyVotingOutcomes.find((item) =>
      dayjs(item.meetingDate).isSame(dayjs(votingOutcomeDateSelected)),
    );
    setCompanyVotingOutcomes(currentVotingOutcome);
  }, [allCompanyVotingOutcomes, votingOutcomeDateSelected]);

  const getColumns = useCallback(
    (proposal) => {
      if (!companyVotingOutcomes || !companyVotingOutcomes?.proposals)
        return [];

      const someProposalHasVotesWithheld = companyVotingOutcomes.proposals.some(
        (p) =>
          (typeof p?.nominees === "object" &&
            p.nominees.some((obj) =>
              Object.keys(obj).includes("votesWithheld"),
            )) ||
          (typeof p === "object" && Object.keys(p).includes("votesWithheld")),
      );

      const isElection = proposal.type === "election";

      let defaultColumns = [
        {
          title: "Votes For",
          dataIndex: "votesFor",
          key: "votesFor",
          render: (val) => (val && !isNaN(val) ? val.toLocaleString() : emdash),
        },
        {
          title: "Votes Withheld",
          dataIndex: "votesWithheld",
          key: "votesWithheld",
          render: (val) => (val && !isNaN(val) ? val.toLocaleString() : emdash),
        },
        {
          title: "Votes Against",
          dataIndex: "votesAgainst",
          key: "votesAgainst",
          render: (val) => (val && !isNaN(val) ? val.toLocaleString() : emdash),
        },
        {
          title: "Abstentions",
          dataIndex: "abstentions",
          key: "abstentions",
          render: (val) => (val && !isNaN(val) ? val.toLocaleString() : emdash),
        },
        {
          title: "Broker Non-Votes",
          dataIndex: "brokerNonVotes",
          key: "brokerNonVotes",
          render: (val) => (val && !isNaN(val) ? val.toLocaleString() : emdash),
        },
      ];

      const filteredColumns = defaultColumns.filter(
        (item) =>
          (typeof proposal?.nominees === "object" &&
            proposal.nominees.some((obj) =>
              Object.keys(obj).includes(item.dataIndex),
            )) ||
          (typeof proposal === "object" &&
            Object.keys(proposal).includes(item.dataIndex)),
      );

      if (
        isElection &&
        !filteredColumns.some((item) => item.title === "Nominee")
      ) {
        filteredColumns.unshift({
          title: "Nominee",
          dataIndex: "name",
          key: "name",
        });
        filteredColumns.push({
          title: (
            <>
              <Tooltip title={approvalDisclaimer}>
                <span style={{ cursor: "help" }}>Approved</span>
              </Tooltip>
            </>
          ),
          dataIndex: "approved",
          key: "approved",
          render: (val, proposal) => {
            return proposal?.approvedPercent > 50 ||
              (proposal?.votesFor || 0) - (proposal?.votesAgainst || 0) > 0
              ? "Yes"
              : "No";
          },
        });
      }
      return filteredColumns;
    },
    [companyVotingOutcomes],
  );

  return (
    <div className="company-calculator-section col-lg-12 executives p-0">
      <h2 style={{ textAlign: "center" }}>Voting Outcomes</h2>
      {votingOutcomeDatesAvailable?.length > 0 && (
        <RadioSelector
          options={votingOutcomeDatesAvailable}
          state={votingOutcomeDateSelected}
          setState={setVotingOutcomeDateSelected}
          heading="Select Meeting Date:"
          style={{ justifyContent: "center", marginBottom: 10 }}
          hoverText={
            "Additional meeting date options will become available as they become publicly available."
          }
        />
      )}
      {companyVotingOutcomes?.meetingDate && (
        <div style={{ textAlign: "center", marginBottom: 20 }}>
          <p>
            <b>Filing Date:</b>{" "}
            {dayjs(companyVotingOutcomes.filingDate).format("M/D/YYYY")}
          </p>
          <p>
            <b>Meeting Date:</b>{" "}
            {dayjs(companyVotingOutcomes.meetingDate).format("M/D/YYYY")}
          </p>
          <p>
            <b>Quorum Met:</b> {companyVotingOutcomes.quorum ? "Yes" : "No"}
          </p>
        </div>
      )}

      {!companyVotingOutcomes || !companyVotingOutcomes?.proposals ? (
        <h3 style={{ textAlign: "center", marginTop: 50 }}>
          Voting Outcomes for {companyInfo?.Ticker} are coming soon
        </h3>
      ) : (
        <>
          {companyVotingOutcomes.proposals.map((proposal) => {
            const isElection = proposal.type === "election";

            const columns = getColumns(proposal);

            const dataSource = isElection
              ? proposal.nominees.map((nominee, idx) => ({
                  key: idx,
                  ...nominee,
                }))
              : [
                  {
                    key: 0,
                    votesFor: proposal.votesFor,
                    votesWithheld: proposal.votesWithheld,
                    votesAgainst: proposal.votesAgainst,
                    abstentions: proposal.abstentions,
                    brokerNonVotes: proposal.brokerNonVotes,
                  },
                ];

            return (
              <div key={proposal.number} style={{ marginBottom: 32 }}>
                <h3 style={{ fontSize: 20 }}>
                  Proposal {proposal.number}: {proposal.title}
                </h3>
                <Table
                  columns={columns}
                  dataSource={dataSource}
                  pagination={false}
                  size="small"
                />
                {proposal?.type !== "election" && (
                  <p>
                    <Tooltip title={approvalDisclaimer}>
                      <span style={{ cursor: "help" }}>
                        <b>Approved:</b>
                      </span>
                    </Tooltip>{" "}
                    {proposal?.approvedPercent > 50 ||
                    proposal?.votesFor - proposal?.votesAgainst > 50
                      ? "Yes"
                      : "No"}
                  </p>
                )}
              </div>
            );
          })}
          <p>
            <b>Source:</b>{" "}
            <a
              href={companyVotingOutcomes.sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              {companyVotingOutcomes.sourceUrl}
            </a>
          </p>
        </>
      )}
    </div>
  );
}
